import * as Yup from 'yup';
import { useWatch } from 'react-hook-form';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import {
  CasLoaderWithBackdrop,
  CasReactHookFormDialog,
  CasRHFSwitch,
} from 'src/compass/shared/components';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { getBooleanSchema } from 'src/compass/shared/schemas';
import { getParsedHTML } from 'src/compass/shared/util/getParsedHTML';
import { IGeneralObjectType } from 'src/compass/site/util';

const SIGNATURE_SETTINGS_URL = 'CurrentUser/SignatureSettings';

const Schema = Yup.object().shape({
  includeThankyouInSignature: getBooleanSchema("Include 'Thank you'", true),
});

type Props = {
  onClose: VoidFunction;
};

/**
 * Preview of the signature as it will be appended to outgoing email. The backend leaves
 * "[THANKYOU]" unresolved in the payload precisely so this can react to the switch immediately,
 * without a save + refetch round-trip.
 */
function SignaturePreview({ signature, thankYouHtml }: { signature: string; thankYouHtml: string }) {
  const includeThankyou = useWatch({ name: 'includeThankyouInSignature' });

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2, px: 1, py: 0.5, bgcolor: '#eee' }}>
        Your signature
      </Typography>
      {getParsedHTML(signature.replace('[THANKYOU]', includeThankyou ? thankYouHtml : ''))}
    </>
  );
}

export default function MySignatureSettingsDialog({ onClose }: Props) {
  const { data, isFetching } = useRetrieve({ url: SIGNATURE_SETTINGS_URL });

  const { mutate } = useUpdateMutation({
    url: SIGNATURE_SETTINGS_URL,
    errorTitle: 'Save signature settings failed',
    onSuccess: () => onClose(),
  });

  const onSubmit = (payload: IGeneralObjectType) => mutate(payload);

  // isFetching, not isLoading: CasReactHookForm seeds useForm's defaultValues once, at mount, so
  // reopening the dialog must wait for the refetch rather than mounting the form against the
  // cached response - otherwise a just-saved change reads as if it never took. Same reason
  // CasEditableDialog / SceReportsDetailsDialog gate on isFetching.
  if (isFetching) return <CasLoaderWithBackdrop />;

  return (
    <CasReactHookFormDialog
      open
      title="My Signature Settings"
      submitButtonLabel="Save"
      onClose={onClose}
      onSubmit={onSubmit}
      schema={Schema}
      defaultValues={{ includeThankyouInSignature: data?.includeThankyouInSignature ?? true }}
    >
      <CasRHFSwitch name="includeThankyouInSignature" label="Include 'Thank you'" />

      <Divider sx={{ mt: 2 }} />

      <SignaturePreview signature={data?.signature ?? ''} thankYouHtml={data?.thankYouHtml ?? ''} />
    </CasReactHookFormDialog>
  );
}
